import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { QrCode, Mail, Key, LogIn, Eye, EyeOff, Building2, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import ServiceMessageBanner from '../components/ServiceMessageBanner';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pauseInfo, setPauseInfo] = useState(null); // {organization_name, reason, message}
  const [showPassword, setShowPassword] = useState(false);
  
  // LØST BUG: "Husk passord"-feltet hadde ingen funksjonalitet
  // Last inn lagrede credentials fra localStorage ved oppstart
  React.useEffect(() => {
    try {
      const savedEmail = localStorage.getItem('saved_email');
      const savedPassword = localStorage.getItem('saved_password');
      const remember = localStorage.getItem('remember_me');
      if (savedEmail && remember === 'true') {
        setFormData((prev) => ({
          ...prev,
          email: savedEmail,
          password: savedPassword || '',
          rememberMe: true,
        }));
      }
    } catch (e) {
      // localStorage kan være blokkert i privat modus - ignorer
    }
  }, []);
  
  // Anti-bot state
  const [formLoadTime] = useState(Date.now());
  const [mathChallenge] = useState(() => {
    const a = Math.floor(Math.random() * 10) + 1;
    const b = Math.floor(Math.random() * 10) + 1;
    return { a, b, answer: a + b };
  });
  const [mathAnswer, setMathAnswer] = useState('');
  const [showMathChallenge, setShowMathChallenge] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  
  // MULTI-CLUB: holds memberships + temp_token when user belongs to multiple clubs
  const [multiOrgData, setMultiOrgData] = useState(null);
  const [pickingOrgId, setPickingOrgId] = useState(null);

  const { login, selectOrganization, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  if (isAuthenticated) {
    // Redirect based on user role after authentication
    let redirectPath = '/';
    
    if (user?.role === 'super_admin') {
      redirectPath = '/super-admin'; // Super admin goes directly to Organizations tab
    } else if (user?.role === 'admin' || user?.role === 'org_admin' || user?.role === 'range_officer') {
      redirectPath = '/admin'; // Admin, org admins and range officers go to Admin page
    }
    
    return <Navigate to={redirectPath} />;
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const navigateByRole = (userRole) => {
    if (userRole === 'super_admin') {
      navigate('/super-admin');
    } else if (userRole === 'admin' || userRole === 'org_admin' || userRole === 'range_officer') {
      navigate('/admin');
    } else {
      navigate('/');
    }
  };

  const handlePickOrganization = async (organizationId) => {
    if (!multiOrgData) return;
    setPickingOrgId(organizationId);
    setError('');
    const result = await selectOrganization(multiOrgData.temp_token, organizationId);
    if (!result.success) {
      setError(result.error || 'Klubbvalg mislyktes');
      setPickingOrgId(null);
      return;
    }
    navigateByRole(result.user?.role);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setPauseInfo(null);
    
    // Calculate form submission time
    const formSubmitTime = (Date.now() - formLoadTime) / 1000; // in seconds
    
    // Prepare anti-bot data
    const antiBotData = {
      honeypot: '', // Always empty (honeypot field)
      form_load_time: formSubmitTime,
      math_answer: mathAnswer ? parseInt(mathAnswer) : null
    };

    const result = await login(formData.email, formData.password, antiBotData);
    
    if (!result.success) {
      // Check if demo expired
      if (result.error === 'demo_expired') {
        navigate('/demo-expired');
        return;
      }
      
      // Check if email not verified
      if (result.error === 'email_not_verified') {
        setError('E-postadressen din er ikke bekreftet ennå. Sjekk e-posten din for verifiseringslenke. Sjekk også spam/søppelpost.');
        setLoading(false);
        return;
      }

      // Check if account is paused (PAUSE-status satt av admin)
      if (result.error === 'account_paused' && result.pauseInfo) {
        setPauseInfo(result.pauseInfo);
        setLoading(false);
        return;
      }
      
      // Check if backend requires CAPTCHA (3+ failed attempts)
      if (result.error === 'Vennligst svar på sikkerhetsspørsmålet') {
        setShowMathChallenge(true);
        setError('For din sikkerhet må du svare på sikkerhetsspørsmålet nedenfor.');
        setLoading(false);
        return;
      }
      
      setError(result.error);
      
      // Show math challenge after 3 failed attempts
      setFailedAttempts(prev => prev + 1);
      if (failedAttempts >= 2) { // Show on 3rd attempt
        setShowMathChallenge(true);
      }
      
      setLoading(false);
    } else if (result.multi_org) {
      // MULTI-CLUB: user belongs to multiple clubs -> show OrganizationPicker
      // LØST BUG: lagre credentials nå (før klubb-velger) hvis "Husk passord" er valgt
      try {
        if (formData.rememberMe) {
          localStorage.setItem('saved_email', formData.email.toLowerCase().trim());
          localStorage.setItem('saved_password', formData.password);
          localStorage.setItem('remember_me', 'true');
        } else {
          localStorage.removeItem('saved_email');
          localStorage.removeItem('saved_password');
          localStorage.removeItem('remember_me');
        }
      } catch (e) { /* ignore */ }
      setMultiOrgData({
        temp_token: result.temp_token,
        memberships: result.memberships,
        full_name: result.full_name,
        email: result.email,
      });
      setFailedAttempts(0);
      setShowMathChallenge(false);
      setLoading(false);
    } else {
      // Reset on successful login
      setFailedAttempts(0);
      setShowMathChallenge(false);
      
      // LØST BUG: lagre credentials hvis "Husk passord" er valgt
      try {
        if (formData.rememberMe) {
          localStorage.setItem('saved_email', formData.email.toLowerCase().trim());
          localStorage.setItem('saved_password', formData.password);
          localStorage.setItem('remember_me', 'true');
        } else {
          localStorage.removeItem('saved_email');
          localStorage.removeItem('saved_password');
          localStorage.removeItem('remember_me');
        }
      } catch (e) { /* ignore */ }
      
      // Redirect based on role
      navigateByRole(result.user?.role);
      setLoading(false);
    }
  };

  // Removed handleForgotPassword - now uses dedicated ForgotPassword page

  // MULTI-CLUB: Show OrganizationPicker after successful auth if user belongs to multiple clubs
  if (multiOrgData) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
        <ServiceMessageBanner />
        <div className="max-w-md w-full space-y-6" data-testid="org-picker-container">
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-yellow-500 rounded-full flex items-center justify-center">
              <Building2 className="h-8 w-8 text-gray-900" />
            </div>
            <h2 className="mt-6 text-3xl font-bold text-white">
              Velg klubb
            </h2>
            <p className="mt-2 text-gray-400">
              Hei, <span className="text-yellow-400 font-semibold">{multiOrgData.full_name || 'medlem'}</span>! Du er medlem i flere klubber – velg hvilken du vil logge inn på.
            </p>
          </div>

          {error && (
            <div className="bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded-lg text-sm" data-testid="org-picker-error">
              {error}
            </div>
          )}

          <div className="space-y-3">
            {multiOrgData.memberships.map((m) => {
              const isPicking = pickingOrgId === m.organization_id;
              const roleLabel = {
                'member': 'Medlem',
                'org_admin': 'Skyteleder',
                'admin': 'Admin',
                'range_officer': 'Skyteleder',
              }[m.role] || m.role;
              return (
                <button
                  key={m.organization_id}
                  onClick={() => handlePickOrganization(m.organization_id)}
                  disabled={!!pickingOrgId}
                  className="w-full flex items-center gap-4 p-4 bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-yellow-500 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
                  data-testid={`org-picker-option-${m.organization_id}`}
                >
                  <div className="flex-shrink-0 h-12 w-12 rounded-lg bg-gray-700 flex items-center justify-center overflow-hidden">
                    {m.organization_logo ? (
                      <img src={m.organization_logo} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <Building2 className="h-6 w-6 text-yellow-500" />
                    )}
                  </div>
                  <div className="flex-1 text-left">
                    <div className="text-white font-semibold">{m.organization_name}</div>
                    <div className="text-xs text-gray-400">
                      {roleLabel}{m.member_number ? ` · ${m.member_number}` : ''}
                    </div>
                  </div>
                  {isPicking ? (
                    <div className="h-5 w-5 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-yellow-400 transition-colors" />
                  )}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => {
              setMultiOrgData(null);
              setError('');
              setFormData({ ...formData, password: '' });
            }}
            className="w-full text-center text-sm text-gray-400 hover:text-yellow-400 transition-colors"
            data-testid="org-picker-back"
          >
            ← Tilbake til innlogging
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
      {/* Service Message Banner */}
      <ServiceMessageBanner />
      
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-yellow-500 rounded-full flex items-center justify-center">
            <QrCode className="h-8 w-8 text-gray-900" />
          </div>
          <h2 className="mt-6 text-3xl font-bold text-white">
            Velkommen til Aktivlogg
          </h2>
          <p className="mt-2 text-gray-400">
            Digital treningslogg for pistolklubber
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                E-post adresse
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoComplete="username"
                  value={formData.email}
                  onChange={handleChange}
                  className="appearance-none relative block w-full px-10 py-3 border border-gray-600 placeholder-gray-400 text-white bg-gray-700 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 focus:z-10 sm:text-sm"
                  placeholder="din@epost.no"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                Passord
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                  <Key className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleChange}
                  className="appearance-none relative block w-full px-10 py-3 pr-10 border border-gray-600 placeholder-gray-400 text-white bg-gray-700 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 sm:text-sm"
                  placeholder="Ditt passord"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center z-20">
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-gray-400 hover:text-gray-300 focus:outline-none focus:text-gray-300 relative z-30"
                    aria-label={showPassword ? "Skjul passord" : "Vis passord"}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
            </div>
            
            {/* Math Challenge - Show after 3 failed attempts */}
            {showMathChallenge && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-md p-4">
                <label htmlFor="mathAnswer" className="block text-sm font-medium text-yellow-400 mb-2">
                  🔢 Sikkerhetsspørsmål: Hva er {mathChallenge.a} + {mathChallenge.b}?
                </label>
                <input
                  id="mathAnswer"
                  name="mathAnswer"
                  type="number"
                  required={showMathChallenge}
                  value={mathAnswer}
                  onChange={(e) => setMathAnswer(e.target.value)}
                  className="appearance-none block w-full px-4 py-2 border border-yellow-500/30 placeholder-gray-400 text-white bg-gray-700 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 sm:text-sm"
                  placeholder="Skriv svaret her"
                />
                <p className="mt-2 text-xs text-gray-400">
                  Dette er for å bekrefte at du ikke er en robot.
                </p>
              </div>
            )}
            
            {/* Honeypot field - hidden from users, bots will fill it */}
            <input
              type="text"
              name="honeypot"
              tabIndex="-1"
              autoComplete="off"
              style={{ position: 'absolute', left: '-9999px' }}
              aria-hidden="true"
            />
            
            {/* Remember Me Checkbox and Forgot Password */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="rememberMe"
                  name="rememberMe"
                  type="checkbox"
                  checked={formData.rememberMe}
                  onChange={handleChange}
                  className="h-4 w-4 text-yellow-500 focus:ring-yellow-500 border-gray-600 bg-gray-700 rounded"
                />
                <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-300">
                  Husk passord
                </label>
              </div>
              
              <Link
                to={`/forgot-password${formData.email ? `?email=${encodeURIComponent(formData.email)}` : ''}`}
                className="text-sm text-yellow-500 hover:text-yellow-400 transition-colors"
              >
                Glemt passord?
              </Link>
            </div>
          </div>

          {error && (
            <div className="bg-red-600/20 border border-red-600/50 rounded-md p-4">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {pauseInfo && (
            <div
              className="bg-yellow-500/15 border-2 border-yellow-500/60 rounded-lg p-4 space-y-2"
              data-testid="login-pause-banner"
            >
              <div className="flex items-center gap-2">
                <span className="text-2xl">🟡</span>
                <h3 className="text-yellow-300 font-bold text-base">
                  Kontoen din er satt på PAUSE
                </h3>
              </div>
              <p className="text-yellow-100 text-sm">
                Din konto i <span className="font-semibold">{pauseInfo.organization_name}</span> har blitt midlertidig deaktivert av en administrator.
              </p>
              <div className="bg-gray-900/40 border border-yellow-500/30 rounded p-3">
                <p className="text-xs text-yellow-200 font-semibold mb-1">Årsak fra klubben:</p>
                <p className="text-sm text-white whitespace-pre-wrap">{pauseInfo.reason}</p>
              </div>
              <p className="text-yellow-200/80 text-xs italic">
                Ta kontakt med klubben din for å ordne opp. Treningsloggen og dataene dine er bevart — du får tilgang igjen så snart administrator reaktiverer kontoen.
              </p>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-gray-900 bg-yellow-500 hover:bg-yellow-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <LogIn className="h-5 w-5 mr-2" />
              {loading ? 'Logger inn...' : 'Logg inn'}
            </button>
          </div>

          <div className="text-center">
            <p className="text-gray-400 text-sm">
              For registrering - kontakt en av dine skyteledere i klubben.
            </p>
          </div>

          {/* Demo credentials section removed for external beta testing */}
        </form>
      </div>
    </div>
  );
};

export default Login;